/**
 * One envelope register line (TMMAPP-0101; audit 06 § B3, v2 L1380–1460): a table row at `md`+, a card
 * below it.
 *
 * - Date `caption` `text2`; Source "{name} ···· {last4}" at 500 — a `link` to the account when the viewer
 *   owns it, else `text` with a lock named "Not your account"; Owner "You" or the owner's name.
 * - Description with a 7px group dot (a per-group data colour, for a group of more than one entry; the
 *   tooltip is text), the Reversed / Reversal / Split markers as bare `micro` tags after it. A plan or
 *   rule origin is its icon; an `AI` origin is the compact AI pill (TMMAPP-0121 row 11, TMM-B9).
 * - Category at rest is a 12px text button with a dotted underline (an "Assign category" `link` when
 *   unset; plain text for a reader who can't write); editing is a native select — "Follow the group",
 *   "None" and EVERY category of the virtual account, whichever way the money moves (MM-D-128: an
 *   incoming line may take an EXPENSE category as a refund). A part with `contra` shows a "Refund" /
 *   "Give-back" `micro` tag after its category. Amounts stay signed.
 * - Amount 500: outgoing `text` with a true minus, incoming `moneyPositive` with "+" (contract v2 §13);
 *   Balance 400 `text2`.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import {
  Box,
  Link,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { Category } from '@/api/categories';
import type { AllocationOrigin, EnvelopeRegisterLine, MovedFrom } from '@/api/registers';
import { AiPill } from '@/components/AiPill';
import { LockIcon, RecurringIcon, RuleIcon } from '@/components/icons';
import { TabularText } from '@/components/TabularText';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';

const DATE_SHORT = { month: 'short', day: 'numeric' } as const;
/** The native select's "no category" value, and the "Follow the group" action's. */
const NONE = '';
const FOLLOW = 'follow';

const ORIGIN_ICON: Partial<Record<AllocationOrigin, typeof RuleIcon>> = {
  PLAN: RecurringIcon,
  RULE: RuleIcon,
};

export interface RegisterRowProps {
  line: EnvelopeRegisterLine;
  /** The virtual account OWNER's currency (§14.1). */
  currency: string | null;
  /** Every category of the virtual account (archived included — a past line may still use one). */
  categories: Category[];
  /** A writer assigns categories inline; a reader sees them. */
  writer: boolean;
  /** A patch is in flight for this line. */
  busy: boolean;
  /** `md`+: a table row; below, a card. */
  wide: boolean;
  /** The category select is open on this line. */
  editing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onSelectCategory: (categoryId: number | null) => void;
  /** Present when the line can re-join its group's category (a loaded peer disagrees). */
  onFollowGroup?: () => void;
  /** The route state a source link carries, so the account knows where you came from. */
  originState: { from: 'envelope'; virtualAccountId: number; envelopeId: number };
}

/** A stable small integer for a group key, so a group keeps one data colour while its rows are loaded. */
function hueOf(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 2147483647;
  return hash;
}

function originTooltip(line: EnvelopeRegisterLine, t: TFunction): string {
  return t(`envelope.origin.${line.origin}`);
}

/** Each `movedFrom` kind's sentence, with and without the served actor (TMMAPI-0152 ask 2). */
const MOVED_KEYS: Record<MovedFrom['kind'], { actor: string; plain: string }> = {
  unassigned: {
    actor: 'envelope.movedFrom.unassigned',
    plain: 'envelope.movedFromPlain.unassigned',
  },
  envelope: { actor: 'envelope.movedFrom.envelope', plain: 'envelope.movedFromPlain.envelope' },
  removed: { actor: 'envelope.movedFrom.removed', plain: 'envelope.movedFromPlain.removed' },
  multiple: { actor: 'envelope.movedFrom.multiple', plain: 'envelope.movedFromPlain.multiple' },
};

/**
 * The re-split tag's tooltip (TMMAPP-0114 B6): "Moved here from {where} by {actor}" from the served
 * `movedFrom` / `actorName`; a part written before tmm-api 0152 reads "Moved here by a re-split".
 */
function movedTooltip(line: EnvelopeRegisterLine, t: TFunction): string {
  const moved = line.movedFrom;
  if (moved === null) return t('envelope.origin.SPLIT');
  const keys = MOVED_KEYS[moved.kind];
  const envelope = moved.kind === 'envelope' ? moved.envelopeName : '';
  return line.actorName !== null
    ? t(keys.actor, { envelope, actor: line.actorName })
    : t(keys.plain, { envelope });
}

function MicroTag({ children, title }: { children: ReactNode; title?: string }) {
  const tokens = resolveTokens(useTheme());
  const tag = (
    <Typography component="span" variant="micro" sx={{ color: tokens.text2, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
  );
  return title === undefined ? tag : <Tooltip title={title}>{tag}</Tooltip>;
}

/** The Description cell's content: the group dot, the description and its markers. */
function Description({ line, struck }: { line: EnvelopeRegisterLine; struck: boolean }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const grouped = line.groupKey !== null && (line.groupSize ?? 0) > 1;
  const OriginIcon = ORIGIN_ICON[line.origin];
  // The served group label names the series (or the payee) the entries matched; the owner on a partner's
  // line is served none, and then the plain wording stands (TMMAPI-0143 / 0150).
  const note =
    line.groupLabel !== null && line.groupLabel.length > 0
      ? t('envelope.groupNoteLabeled', { count: line.groupSize ?? 0, label: line.groupLabel })
      : t('envelope.groupNote', { count: line.groupSize ?? 0 });
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}
    >
      {grouped && (
        <Tooltip title={note}>
          <Box
            component="span"
            role="img"
            aria-label={note}
            data-group-dot=""
            sx={{
              flex: '0 0 7px',
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: categoricalFor(hueOf(line.groupKey ?? ''), tokens).fill,
            }}
          />
        </Tooltip>
      )}
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
          overflowWrap: 'anywhere',
          ...(struck ? { textDecoration: 'line-through', color: tokens.text2 } : {}),
        }}
      >
        {line.payee}
      </Typography>
      {struck && <MicroTag>{t('status.registerLine.REVERSED')}</MicroTag>}
      {line.reversalOfAllocationId !== null && <MicroTag>{t('envelope.reversalOf')}</MicroTag>}
      {(line.origin === 'SPLIT' || line.movedFrom !== null) && (
        <MicroTag title={movedTooltip(line, t)}>{t('envelope.splitTag')}</MicroTag>
      )}
      {line.origin === 'AI' && line.movedFrom === null && <AiPill compact describes="allocation" />}
      {OriginIcon && line.origin !== 'SPLIT' && line.movedFrom === null && (
        <Tooltip title={originTooltip(line, t)}>
          <Box
            component="span"
            role="img"
            aria-label={originTooltip(line, t)}
            sx={{ display: 'inline-flex', color: tokens.text2 }}
          >
            <OriginIcon size={14} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

/** The Category cell: the resting text button, the native select, or plain text for a reader. */
function CategoryCell({
  line,
  categories,
  writer,
  busy,
  editing,
  onStartEdit,
  onStopEdit,
  onSelectCategory,
  onFollowGroup,
}: Pick<
  RegisterRowProps,
  | 'line'
  | 'categories'
  | 'writer'
  | 'busy'
  | 'editing'
  | 'onStartEdit'
  | 'onStopEdit'
  | 'onSelectCategory'
  | 'onFollowGroup'
>) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const select = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (editing) select.current?.focus();
  }, [editing]);
  const current = categories.find((c) => c.id === line.categoryId) ?? null;
  const contra =
    line.contra !== null ? <MicroTag>{t(`allocate.contra.${line.contra}`)}</MicroTag> : null;

  if (editing && writer) {
    // Every category of the virtual account, whichever way the money moves (MM-D-128); the line's own
    // category stays listed even when archived so the control can show it.
    const offered = categories.filter((c) => c.archivedAt === null || c.id === line.categoryId);
    return (
      <Select
        native
        size="small"
        value={line.categoryId === null ? NONE : String(line.categoryId)}
        disabled={busy}
        inputRef={select}
        onBlur={onStopEdit}
        onChange={(event) => {
          const value = String(event.target.value);
          if (value === FOLLOW) onFollowGroup?.();
          else onSelectCategory(value === NONE ? null : Number(value));
        }}
        inputProps={{ 'aria-label': t('envelope.colCategory') }}
        sx={{
          minWidth: 104,
          maxWidth: 160,
          fontSize: 12,
          '& .MuiNativeSelect-select': { py: '5px' },
        }}
      >
        {onFollowGroup !== undefined && (
          <option value={FOLLOW}>↺ {t('envelope.followGroup')}</option>
        )}
        <option value={NONE}>{t('envelope.categoryNone')}</option>
        {offered.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {c.name}
          </option>
        ))}
      </Select>
    );
  }

  const restingSx = {
    p: '4px 0',
    border: 0,
    bgcolor: 'transparent',
    fontFamily: 'inherit',
    fontSize: 12,
    textAlign: 'start',
    cursor: 'pointer',
  } as const;
  if (!writer) {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, fontSize: 12 }}
      >
        <span>{current?.name ?? t('envelope.uncategorized')}</span>
        {contra}
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
      {current !== null ? (
        <Box
          component="button"
          type="button"
          onClick={onStartEdit}
          disabled={busy}
          aria-label={t('envelope.changeCategory', { name: current.name })}
          sx={{
            ...restingSx,
            maxWidth: 118,
            color: tokens.text,
            borderBottom: `1px dotted ${tokens.text3}`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&:hover': { color: tokens.link, borderBottomColor: tokens.link },
          }}
        >
          {current.name}
        </Box>
      ) : (
        <Link
          component="button"
          type="button"
          onClick={onStartEdit}
          disabled={busy}
          underline="hover"
          sx={{ ...restingSx, color: tokens.link, whiteSpace: 'nowrap' }}
        >
          {t('envelope.assignCategory')}
        </Link>
      )}
      {contra}
    </Box>
  );
}

export function RegisterRow(props: RegisterRowProps) {
  const { line, currency, wide, originState } = props;
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  // TMMAPP-0135 item 6: a reversed line (the served `reversedByAllocationId`) is struck in `text2`.
  const struck = line.reversedByAllocationId !== null;
  const account = line.account;
  const own = account.viewerOwns;
  const sourceName =
    account.last4 === null
      ? account.name
      : t('virtualAccount.ledger.source', { account: account.name, last4: account.last4 });
  const source = own ? (
    <Link
      component={RouterLink}
      to={`/me/accounts/${account.id}`}
      state={originState}
      underline="hover"
      sx={{ fontWeight: 500, color: tokens.link }}
    >
      {sourceName}
    </Link>
  ) : (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <Box component="span" sx={{ fontWeight: 500 }}>
        {sourceName}
      </Box>
      <Box
        component="span"
        role="img"
        aria-label={t('envelope.notYourAccount')}
        sx={{ display: 'inline-flex', color: tokens.textLabel('surface') }}
      >
        <LockIcon size={12} />
      </Box>
    </Box>
  );
  const owner = own ? t('share.you') : personName(account.ownerDisplayName, t);
  const amount = (size: number) => (
    <Box
      component="span"
      sx={{
        fontSize: size,
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        color: struck ? tokens.text2 : registerAmountColor(tokens, line.amountMinor),
        ...(struck ? { textDecoration: 'line-through' } : {}),
      }}
    >
      <MoneyParts minor={line.amountMinor} currency={currency} signed />
    </Box>
  );
  // The running balance: a string inside the narrow card's sentence, an amount node in its cell.
  const running =
    line.runningBalanceMinor === null
      ? '—'
      : formatMoneyMinor(line.runningBalanceMinor, currency, locale);
  const runningNode =
    line.runningBalanceMinor === null ? (
      '—'
    ) : (
      <MoneyParts minor={line.runningBalanceMinor} currency={currency} />
    );
  const date = formatCivilDate(line.postedOn, locale, DATE_SHORT);
  const cell = { fontSize: 13, verticalAlign: 'middle' } as const;

  if (!wide) {
    return (
      <Box
        data-register-card={line.id}
        sx={{
          p: '14px 16px',
          borderBottom: `1px solid ${tokens.line}`,
          display: 'grid',
          gap: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            alignItems: 'baseline',
          }}
        >
          <Box sx={{ fontSize: 14, fontWeight: 500, minWidth: 0 }}>
            <Description line={line} struck={struck} />
          </Box>
          {amount(15)}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            fontSize: 12,
            color: tokens.text2,
          }}
        >
          <Box component="span">{source}</Box>
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {t('envelope.balanceShort', { amount: running })}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            columnGap: 0.75,
            fontSize: 12,
            color: tokens.textLabel('surface'),
          }}
        >
          <span>
            {date} · {owner} ·
          </span>
          <CategoryCell {...props} />
        </Box>
      </Box>
    );
  }

  return (
    <TableRow hover data-register-row={line.id}>
      <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
        <TabularText sx={{ color: tokens.text2, fontSize: 12 }}>{date}</TabularText>
      </TableCell>
      <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{source}</TableCell>
      <TableCell sx={{ ...cell, fontSize: 12, color: tokens.text2, whiteSpace: 'nowrap' }}>
        {owner}
      </TableCell>
      <TableCell sx={cell}>
        <Description line={line} struck={struck} />
      </TableCell>
      <TableCell sx={{ ...cell, px: '6px' }}>
        <CategoryCell {...props} />
      </TableCell>
      <TableCell align="right" sx={cell}>
        {amount(13)}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          ...cell,
          color: tokens.text2,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {runningNode}
      </TableCell>
    </TableRow>
  );
}
